import _debounce from 'lodash.debounce';
import SidebarMenuHandler from "./handlers/sidebar-menu-handler";

/**
 * Adjusts elements' bottom position on mobile devices
 *
 * THE PROBLEM:
 * You make an element stick to the bottom of the page. You see it on a mobile browser,
 * like Safari or Chrome, and the browser's UI covers the element.
 *
 * THE SOLUTION:
 * We calculate the inner height of the viewport (excludes browser's UI elements) and set
 * the "TOP" position of the "fixed" element.
 * Calculating the bottom space doesn't work because the viewport difference you would get
 * will include both the "top" and "bottom" parts of the browser, and only the "bottom"
 * bit is the issue.
 *
 * HOW THIS WORKS:
 * Add the class defined in "_watchSelector" to any element you want to target and the
 * adjustment will take place when the utility is initialised (i.e. page load) as well
 * as when the browser resizes.
 * You can also manually trigger the refresh from anywhere in the application by dispatching
 * the 'viewport-flex:refresh-positioning' custom event.
 *
 * Additionally, you can execute additional adjustments to other elements when a
 * watched element's position changes via handler services. They listen to the
 * position change event that is dispatched and execute further actions.
 * This is useful for cases where you want to sticky the footer of a sidebar menu
 * to the bottom of the viewport and have the sidebar menu itself to adjust its
 * margin accordingly.
 *
 * If the device doesn't support touch events, the utility does nothing.
 */
const ViewportFlexItems = {
  // Add this class to elements you want to watch
  _watchSelector: '.el-bottom-position-watched',

  _initialViewportHeight: 0,

  init() {
    const self = this;

    // We only care about touch devices
    if ( ! this._isTouchDevice() ) {
      return;
    }

    // Enables custom handlers
    SidebarMenuHandler.enable();

    // Captures the initial height when the page loads to be used as reference when the UI
    // is hidden by scrolling down.
    this._initialViewportHeight = window.innerHeight;

    // Event :: adjusts position on all watched elements when browser resizes
    window.addEventListener('resize', _debounce(() => {
      self.refreshPositioning();
    }, 200));

    // Event :: allows other components to trigger a "refresh" action
    document.addEventListener('viewport-flex:refresh-positioning', () => {
      self.refreshPositioning();
    });

    // Initial adjustment on page load
    this.refreshPositioning();
  },

  /**
   * You can manually call this method to trigger the re-positioning of elements
   */
  refreshPositioning() {
    // We only care about touch devices, remove watcher class
    if ( ! this._isTouchDevice() ) {
      return;
    }

    document.querySelectorAll(this._watchSelector).forEach(el => {

      // Gets position before making adjustment
      const initialElementPosition = el.getBoundingClientRect().bottom;

      el.style.position = 'fixed';
      el.style.top = parseInt(window.innerHeight - el.offsetHeight) + 'px';
      el.style.bottom = 'auto'; // Ensures not bottom position is set

      // Reads new position after adjustment
      const currentElementPosition = el.getBoundingClientRect().bottom;
      const changedPositionAmount = initialElementPosition - currentElementPosition;

      // Stores initial position change as reference for other adjustments done by handler services
      if ( el.getAttribute('data-pos-change-amount') === null ) {
        el.setAttribute('data-pos-change-amount', changedPositionAmount);
      }

      // Event dispatcher :: allows handler services to hook and preform other adjustments
      const elPositionChangeEvent = new CustomEvent('viewport-flex-item:refreshed', {
        detail: {
          el,
          hasViewportGrown: window.innerHeight > this._initialViewportHeight
        }
      });
      document.dispatchEvent(elPositionChangeEvent);
    });
  },

  _isTouchDevice() {
    return (('ontouchstart' in window) ||
      (navigator.maxTouchPoints > 0) ||
      (navigator.msMaxTouchPoints > 0))
    ;
  }
};

export default ViewportFlexItems;